import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { StyledButton } from "../../doctor-console/StyledComponents";

const clinicalStatusOptions = [
  "Active",
  "Inactive",
  "Resolved",
  "Remission",
  "Recurrence",
];

function getDefaultRow(columns) {
  const base = columns.reduce((acc, col) => {
    acc[col.key] = col.defaultValue ?? "";
    return acc;
  }, {});
  if (columns.some((col) => col.key === "observation_date")) {
    base.observation_date_manual = false;
  }
  return base;
}

export default function PhysioEditableSection({
  title,
  rows,
  setRows,
  columns,
  onCdacSearch,
  requiredFields = [],
  maxEntries = null,
}) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState(getDefaultRow(columns));
  const [cdacOptions, setCdacOptions] = useState({});
  const [focusedDateField, setFocusedDateField] = useState("");
  const debounceTimeoutRef = useRef(null);
  const [validationErrors, setValidationErrors] = useState({});

  const defaultRow = useMemo(() => getDefaultRow(columns), [columns]);

  const resetForm = () => {
    setForm(defaultRow);
    setEditingIndex(null);
    setCdacOptions({});
    setValidationErrors({});
  };

  const handleChange = (key, value, col) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "observation_date" && col?.type === "date") {
        next.observation_date_manual = true;
      }
      return next;
    });
  };

  const handleCdacInput = async (col, value) => {
    handleChange(col.key, value);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    if (!value || value.trim().length < 2) {
      setCdacOptions((prev) => ({ ...prev, [col.key]: [] }));
      return;
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      const suggestions = await onCdacSearch(value, col.semanticTag);
      setCdacOptions((prev) => ({ ...prev, [col.key]: suggestions || [] }));
    }, 1000);
  };

  const handlePickCdac = (col, option) => {
    setForm((prev) => ({
      ...prev,
      [col.key]: option.term,
      [col.codeKey]: option.id || null,
    }));
    setCdacOptions((prev) => ({ ...prev, [col.key]: [] }));
  };

  const saveRow = () => {
    const nextErrors = {};
    requiredFields.forEach((fieldKey) => {
      const value = form[fieldKey];
      if (value === undefined || value === null || String(value).trim() === "") {
        nextErrors[fieldKey] = "This field is required.";
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setValidationErrors(nextErrors);
      return;
    }
    if (
      editingIndex === null &&
      Number.isInteger(maxEntries) &&
      (rows || []).length >= maxEntries
    ) {
      return;
    }

    if (editingIndex !== null) {
      const updated = rows.map((item, idx) => (idx === editingIndex ? form : item));
      setRows(updated);
    } else {
      setRows([...(rows || []), form]);
    }
    resetForm();
  };

  const editRow = (idx) => {
    setEditingIndex(idx);
    setForm(rows[idx]);
    setValidationErrors({});
  };

  const deleteRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
    if (editingIndex === idx) resetForm();
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: "10px",
        border: "1px solid #D9E2EC",
        overflow: "visible", // allow suggestions dropdown to float outside card
      }}
    >
      <Typography sx={{ fontWeight: 700, color: "#1c2c4c", mb: 1.5 }}>
        {title}
      </Typography>

      <Grid
        container
        spacing={1.5}
        sx={{ mb: 1.5, overflow: "visible", alignItems: "flex-start" }}
      >
        {columns.map((col) => (
          <Grid item xs={12} sm={6} md={col.width || 3} key={col.key}>
            {col.type === "select" ? (
              <TextField
                select
                fullWidth
                size="small"
                label={col.label}
                value={form[col.key] ?? ""}
                onChange={(e) => handleChange(col.key, e.target.value, col)}
                error={Boolean(validationErrors[col.key])}
                helperText={validationErrors[col.key] || undefined}
              >
                {(col.options || clinicalStatusOptions).map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  size="small"
                  label={col.label}
                  type={
                    col.type === "date"
                      ? focusedDateField === col.key || Boolean(form[col.key])
                        ? "date"
                        : "text"
                      : col.type || "text"
                  }
                  value={form[col.key] ?? ""}
                  onChange={(e) =>
                    col.type === "cdac"
                      ? handleCdacInput(col, e.target.value)
                      : handleChange(col.key, e.target.value, col)
                  }
                  error={Boolean(validationErrors[col.key])}
                  helperText={validationErrors[col.key] || undefined}
                  multiline={Boolean(col.multiline)}
                  minRows={col.multiline ? 1 : undefined}
                  maxRows={col.multiline ? 4 : undefined}
                  onFocus={() => {
                    if (col.type === "date") setFocusedDateField(col.key);
                  }}
                  onBlur={() => {
                    if (col.type === "date") setFocusedDateField("");
                  }}
                  InputLabelProps={
                    col.type === "date"
                      ? { shrink: focusedDateField === col.key || Boolean(form[col.key]) }
                      : undefined
                  }
                  inputProps={
                    col.type === "date"
                      ? {
                        placeholder: "",
                      }
                      : undefined
                  }
                  sx={
                    col.type === "date"
                      ? {
                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          opacity: 1,
                          display: "block",
                          cursor: "pointer",
                        },
                      }
                      : undefined
                  }
                />
                {col.type === "cdac" && (cdacOptions[col.key] || []).length > 0 ? (
                  <Card
                    sx={{
                      position: "absolute",
                      zIndex: 2000,
                      width: "100%",
                      maxHeight: 160,
                      overflowY: "auto",
                      mt: 0,
                    }}
                  >
                    {(cdacOptions[col.key] || []).map((option) => (
                      <Box
                        key={`${col.key}-${option.id}-${option.term}`}
                        onClick={() => handlePickCdac(col, option)}
                        sx={{
                          p: 1,
                          cursor: "pointer",
                          borderBottom: "1px solid #EEF2F6",
                          "&:hover": { backgroundColor: "#F6FAFF" },
                        }}
                      >
                        <Typography sx={{ fontSize: 13 }}>{option.term}</Typography>
                      </Box>
                    ))}
                  </Card>
                ) : null}
              </Box>
            )}
          </Grid>
        ))}

        <Grid
          item
          xs={12}
          sm={6}
          md={2}
          sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
        >
          <StyledButton
            variant="contained"
            bgColor="#0E3F66"
            sx={{ width: { xs: "100%", md: "100%" } }}
            onClick={saveRow}
            disabled={
              editingIndex === null &&
              Number.isInteger(maxEntries) &&
              (rows || []).length >= maxEntries
            }
          >
            {editingIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>
      </Grid>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          {(rows || []).length > 0 ? (
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.key} sx={{ fontWeight: 700 }}>
                    {col.tableLabel || col.label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
          ) : null}
          <TableBody>
            {(rows || []).map((row, idx) => (
              <TableRow key={`${title}-${idx}`}>
                {columns.map((col) => (
                  <TableCell key={`${col.key}-${idx}`}>
                    <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {row[col.key] === null || row[col.key] === ""
                        ? "-"
                        : String(row[col.key])}
                    </Box>
                  </TableCell>
                ))}
                <TableCell>
                  <EditIcon
                    sx={{ cursor: "pointer", mr: 1, fontSize: 18 }}
                    onClick={() => editRow(idx)}
                  />
                  <DeleteIcon
                    sx={{ cursor: "pointer", fontSize: 18 }}
                    onClick={() => deleteRow(idx)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
